import * as React from 'react';
import { gql } from '@apollo/client';
import { OperationResult } from '@bondvet/types';
import isEmail from 'validator/lib/isEmail';
import client from 'lib/apollo/client';
import { GRAPHQL_CLIENT_NAMES } from 'lib/types';
import useTrackEmailSignup from 'analytics/ttd/hooks/useTrackEmailSignup';
import useGoogleAnalytics from 'analytics/hooks/useGoogleAnalytics';

const mutation = gql`
    mutation subscribeNewsletter($email: String!, $list: String) {
        addMailToList(email: $email, list: $list) {
            success
            error
        }
    }
`;

type Status =
    | 'notSubmitted'
    | 'invalidEmail'
    | 'submitting'
    | 'success'
    | 'failed';

export interface UseNewsletterSignup {
    handleSubmit: (event: React.ChangeEvent<HTMLFormElement>) => Promise<void>;
    handleChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
    email: string;
    isValidEmail: boolean;
    status: Status;
    success: boolean;
}

interface SignupVariables {
    email: string;
    list: string | null;
}

interface SignupResult {
    addMailToList: OperationResult;
}

export default function useNewsletterSignup(
    list: string | null,
): UseNewsletterSignup {
    const [email, setEmail] = React.useState('');
    const [status, setStatus] = React.useState<Status>('notSubmitted');
    const trackEmailSignup = useTrackEmailSignup();
    const { trackWithAnyGoogleAnalytics } = useGoogleAnalytics();

    const isValidEmail = React.useMemo(() => {
        return isEmail(email);
    }, [email]);

    const handleSubmit = React.useCallback(
        (event: React.ChangeEvent<HTMLFormElement>) => {
            event.preventDefault();

            trackEmailSignup();

            async function submit() {
                setStatus('submitting');

                try {
                    const result = await client.mutate<
                        SignupResult,
                        SignupVariables
                    >({
                        mutation,
                        context: {
                            clientName: GRAPHQL_CLIENT_NAMES.newsletter,
                        },
                        variables: { email, list },
                    });

                    if (!result.data?.addMailToList?.success) {
                        console.error(
                            'addMailToList error: ',
                            result.data?.addMailToList?.error,
                        );
                    }
                } catch (error) {
                    console.error('addMailToList error: ', error);
                    setStatus('failed');
                }

                setEmail('');
                setStatus('success');

                trackWithAnyGoogleAnalytics(
                    false,
                    'newsletter_subscription',
                    email,
                    list,
                );
            }

            if (status === 'submitting' || status === 'success') {
                return Promise.resolve();
            }

            if (!isValidEmail) {
                setStatus('invalidEmail');
                return Promise.resolve();
            }

            return submit();
        },
        [
            status,
            isValidEmail,
            email,
            list,
            trackEmailSignup,
            trackWithAnyGoogleAnalytics,
        ],
    );

    const handleChangeEmail = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
        },
        [],
    );

    return {
        handleSubmit,
        handleChangeEmail,
        email,
        isValidEmail,
        status,
        success: status === 'success',
    };
}
